import { useApolloClient } from "@apollo/client";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useUserData } from "../../auth/useUserData.js";
import { useInviteOrganisationMemberMutation } from "./InviteOrganisationMemberModal.generated.js";
import {
  type OrganisationMembershipInvitationEmailFormValues,
  useOrganisationMembershipInvitationEmailForm,
} from "./useOrganisationMembershipInvitationEmailForm.js";

export type { OrganisationMembershipInvitationEmailFormValues } from "./useOrganisationMembershipInvitationEmailForm.js";

export function useOrganisationMembershipInvitation({
  refetchQueries,
}: {
  refetchQueries: string[];
}) {
  const viewer = useUserData().currentUser!;
  const { t } = useTranslate(["OrganisationInvite"]);

  const { formFields, initialValues, validationSchema } =
    useOrganisationMembershipInvitationEmailForm();

  const client = useApolloClient();
  const [invite, { loading: isLoading }] = useInviteOrganisationMemberMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const createSubmitHandler = React.useCallback(
    (userId: string) =>
      async (values: OrganisationMembershipInvitationEmailFormValues) => {
        await invite({
          variables: {
            input: {
              userId,
              messageSubject: t("Join {organisationName} on MeisterSystems", {
                ns: "OrganisationInvite",
                organisationName: viewer.organisation.title,
              }),
              messageBody: values.message,
            },
          },
        });
      },
    [invite, t, viewer.organisation.title]
  );

  return {
    formFields,
    validationSchema,
    initialValues,
    createSubmitHandler,
    isLoading,
  };
}
